import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Plus,
  Clock,
  MapPin,
  Users,
  AlertCircle,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function TrainerReservationsModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin", "packages"]);

  const [trainerId, setTrainerId] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [trainerInfo, setTrainerInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  
  // Form state
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");
  const [sessionType, setSessionType] = useState("one_on_one");
  const [notes, setNotes] = useState("");
  
  // Availability check
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  
  // Date picker popover state for mobile
  const [trainerResDatePickerOpen, setTrainerResDatePickerOpen] = useState(false);

  useEffect(() => {
    if (user?.user_id) {
      fetchTrainerInfo();
    }
  }, [user]);

  const fetchTrainerInfo = async () => {
    try {
      const trainerRes = await fetch(`${API}/trainers/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (trainerRes.ok) {
        const trainers = await trainerRes.json();
        if (trainers.length > 0) {
          const trainer = trainers[0];
          setTrainerId(trainer.trainer_id);
          setBusinessId(trainer.business_id);
          setTrainerInfo(trainer);
          
          await fetchData(trainer.trainer_id, trainer.business_id);
        }
      }
    } catch (error) {
      console.error("Error fetching trainer info:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (tId, bizId) => {
    try {
      const [studentsRes, roomsRes, reservationsRes] = await Promise.all([
        fetch(`${API}/trainers/${tId}/students`, { credentials: "include" }),
        fetch(`${API}/businesses/${bizId}/rooms`, { credentials: "include" }),
        fetch(`${API}/trainers/${tId}/reservations`, { credentials: "include" }),
      ]);

      if (studentsRes.ok) {
        const studentsData = await studentsRes.json();
        // Only show students with active packages
        setStudents(studentsData.filter(s => s.active_package || s.remaining_sessions > 0));
      }
      if (roomsRes.ok) setRooms(await roomsRes.json());
      if (reservationsRes.ok) {
        setReservations(await reservationsRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const checkAvailability = async () => {
    if (!selectedDate || !selectedTime || !trainerId) return;
    
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const endTime = calculateEndTime(selectedTime);
    
    try {
      // Check trainer availability
      const trainerRes = await fetch(
        `${API}/trainers/${trainerId}/availability?date=${dateStr}&time=${selectedTime}&end_time=${endTime}`,
        { credentials: "include" }
      );
      
      if (trainerRes.ok) {
        const data = await trainerRes.json();
        setAvailabilityStatus({
          trainer: data.is_available,
          overlapping: data.overlapping_reservations || []
        });
      }
      
      // Check room availability if selected
      if (selectedRoomId) {
        const roomRes = await fetch(
          `${API}/rooms/${selectedRoomId}/availability?date=${dateStr}&time=${selectedTime}&end_time=${endTime}`,
          { credentials: "include" }
        );
        if (roomRes.ok) {
          const roomData = await roomRes.json();
          setAvailabilityStatus(prev => ({
            ...prev,
            room: roomData.is_available
          }));
        }
      }
    } catch (error) {
      console.error("Error checking availability:", error);
    }
  };

  useEffect(() => {
    if (selectedDate && selectedTime && trainerId) {
      checkAvailability();
    }
  }, [selectedDate, selectedTime, selectedRoomId, trainerId]);

  const calculateEndTime = (startTime) => {
    if (!startTime) return "";
    const [hours, minutes] = startTime.split(":").map(Number);
    const endMinutes = minutes + 55;
    const endHours = hours + Math.floor(endMinutes / 60);
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
  };

  const handleCreateReservation = async () => {
    if (!selectedMemberId || !selectedDate || !selectedTime) {
      toast.error(t("common:toast_ogrenciTarihVeSaatSecmelisiniz"));
      return;
    }

    if (availabilityStatus && !availabilityStatus.trainer) {
      toast.error(t("common:toast_buSaatteBaskaBirDersiniz"));
      return;
    }

    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const endTime = calculateEndTime(selectedTime);

    try {
      const response = await fetch(`${API}/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: businessId,
          member_id: selectedMemberId,
          trainer_id: trainerId,
          room_id: selectedRoomId === "none" ? null : (selectedRoomId || null),
          date: dateStr,
          start_time: selectedTime,
          end_time: endTime,
          session_type: sessionType,
          notes: notes || null,
          status: "confirmed", // Trainer can directly confirm
        }),
      });

      if (response.ok) {
        toast.success(t('reservations:reservationCreated'));
        setShowModal(false);
        resetForm();
        if (trainerId && businessId) {
          fetchData(trainerId, businessId);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:reservationFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    }
  };

  const resetForm = () => {
    setSelectedMemberId("");
    setSelectedRoomId("");
    setSelectedDate(new Date());
    setSelectedTime("");
    setSessionType("one_on_one");
    setNotes("");
    setAvailabilityStatus(null);
  };

  // Generate time slots
  const timeSlots = [];
  for (let h = 7; h < 22; h++) {
    timeSlots.push(`${String(h).padStart(2, "0")}:00`);
    timeSlots.push(`${String(h).padStart(2, "0")}:30`);
  }

  // Today's reservations
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todayReservations = reservations.filter(r => r.date === todayStr);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  if (!trainerInfo) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-12 text-center">
          <CalendarIcon className="w-12 h-12 text-grey mx-auto mb-3" />
          <p className="text-grey">{t("common:trainerInfoNotFound")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">{t("reservations:createReservation")}</h2>
          <p className="text-grey text-sm">{t("admin:ogrencilerinizIcinDersPlaniOlusturun")}</p>
        </div>

        <Button
          className="bg-navy hover:bg-navy/90 rounded-xl"
          onClick={() => setShowModal(true)}
          disabled={students.length === 0}
          data-testid="create-reservation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Rezervasyon
        </Button>
      </div>

      {/* Today's Schedule */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-navy" />
            Bugünkü Dersler ({format(new Date(), "d MMMM", { locale: getDateLocale() })})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {todayReservations.length > 0 ? (
            <div className="space-y-3">
              {todayReservations.map((res) => (
                <div
                  key={res.reservation_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="font-bold text-navy">{res.start_time}</p>
                      <p className="text-xs text-grey">{res.end_time}</p>
                    </div>
                    <div>
                      <p className="font-medium">{res.member_name || "Öğrenci"}</p>
                      <div className="flex items-center gap-2 text-sm text-grey">
                        <Badge variant="secondary" className="text-xs">
                          {res.session_type === "one_on_one" ? t("common:oneOnOne") : 
                           res.session_type === "duet" ? t("common:duet") : "Grup"}
                        </Badge>
                        {res.room_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {res.room_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge 
                    className={
                      res.status === "confirmed" ? "bg-green-100 text-green-700" :
                      res.status === "completed" ? "bg-blue-100 text-blue-700" :
                      "bg-yellow-100 text-yellow-700"
                    }
                  >
                    {res.status === "confirmed" ? t('admin:approved') :
                     res.status === "completed" ? t("common:completed") : "Bekliyor"}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("common:bugunIcinDersBulunmuyor")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Students with Active Packages */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium">{t("packages:aktifPaketliOgrenciler")}</CardTitle>
        </CardHeader>
        <CardContent>
          {students.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {students.map((student) => (
                <div
                  key={student.member_id}
                  className="flex items-center gap-3 p-3 border rounded-xl"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-navy text-white">
                      {student.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{student.name} {student.surname}</p>
                    <p className="text-xs text-grey">{student.remaining_sessions || 0} seans kaldı</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("packages:aktifPaketliOgrenciBulunmuyor")}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Reservation Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('reservations:newReservation')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Student Selection */}
            <div>
              <Label>Öğrenci *</Label>
              <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_ogrenciSecin")} />
                </SelectTrigger>
                <SelectContent>
                  {students.map((student) => (
                    <SelectItem key={student.member_id} value={student.member_id}>
                      {student.name} {student.surname} ({student.remaining_sessions || 0} seans)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div>
              <Label>{t("common:dateRequired")}</Label>
              <Popover open={trainerResDatePickerOpen} onOpenChange={setTrainerResDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-xl"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, "d MMMM yyyy", { locale: getDateLocale() }) : t("common:selectDate")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setTrainerResDatePickerOpen(false);
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    locale={tr}
                    initialFocus={false}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Selection */}
            <div>
              <Label>Saat *</Label>
              <Select value={selectedTime} onValueChange={setSelectedTime}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_saatSecin")} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {timeSlots.map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Availability Status */}
            {availabilityStatus && (
              <div className={`p-3 rounded-xl ${availabilityStatus.trainer ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                <div className="flex items-center gap-2">
                  {availabilityStatus.trainer ? (
                    <>
                      <CalendarIcon className="w-4 h-4" />
                      <span className="text-sm">{t("common:buSaatMusait")}</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm">Bu saatte başka bir dersiniz var!</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Room Selection */}
            <div>
              <Label>Oda (Opsiyonel)</Label>
              <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("common:ph_odaSecin")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("common:odaSecilmedi")}</SelectItem>
                  {rooms.map((room) => (
                    <SelectItem key={room.room_id} value={room.room_id}>
                      {room.name} (Kapasite: {room.capacity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Session Type */}
            <div>
              <Label>{t('reservations:lessonType')}</Label>
              <Select value={sessionType} onValueChange={setSessionType}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_on_one">{t('reservations:private')}</SelectItem>
                  <SelectItem value="duet">{t('reservations:duet')}</SelectItem>
                  <SelectItem value="group">{t('reservations:group')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label>Notlar</Label>
              <Textarea
                placeholder="Ders ile ilgili notlar..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-xl"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                İptal
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleCreateReservation}
                disabled={!selectedMemberId || !selectedDate || !selectedTime || (availabilityStatus && !availabilityStatus.trainer)}
                data-testid="confirm-reservation-btn"
              >
                Rezervasyon Oluştur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
